import { Link, Redirect, useLocation } from "wouter";
import { Icon } from "../../web/components/Icon";
import { ErrorNotice, Spinner } from "../../web/components/ui";
import { BundlesList } from "./BundlesList";
import { usePriceList } from "./data";
import { GroupsList } from "./GroupsList";
import { MaterialsList } from "./MaterialsList";
import { meta } from "./meta";
import { SuppliersList } from "./SuppliersList";
import { usePriceListWindows } from "./windows";

/*
 * The Price List's pages (under /t/pricelist): /materials (Items: ServiceM8's materials; also the tool's own address) and /bundles;
 * then the Catalogue's (catalogue.ts, its own entry in the sidebar): /products (MaterialsList's), /groups (an old /strategies link
 * too) and /suppliers. Its settings are in Settings › Price List (SettingsSection.tsx; old /settings and /catalogue-settings links
 * go there). Clicking a record opens its window over the page (windows.ts; the windows themselves are drawn by WindowHost.tsx, on any page).
 */

const BASE = `/t/${meta.id}`;
const SECTIONS = ["materials", "bundles", "products", "groups", "suppliers"] as const;
type Section = (typeof SECTIONS)[number];

export default function PriceList() {
    const [location] = useLocation();
    const windows = usePriceListWindows();
    const { params, set } = windows;
    const { data, error, isLoading } = usePriceList();
    const first = location.slice(BASE.length).split("/").find(Boolean);
    // Old links: /strategies is /groups now.
    const named = first === "strategies" ? "groups" : first;
    const section: Section = SECTIONS.includes(named as Section) ? (named as Section) : "materials";

    // Old links to its settings pages: Settings › Price List, at the same panel (the Catalogue's: its first).
    if (first === "settings" || first === "catalogue-settings") {
        const at = globalThis.location.hash || (first === "catalogue-settings" ? "#tax-on-purchases" : "");
        return <Redirect to={`/settings/${meta.id}${params.toString() ? "?" + params.toString() : ""}${at}`} replace />;
    }
    if (isLoading) return <Spinner full />;
    if (!data) {
        return (
            <div className="page">
                <ErrorNotice error={error ?? new Error("The Price List couldn't be loaded.")} />
            </div>
        );
    }

    return (
        <>
            {/* On wide screens the sidebar lists these under the Price List (components/Shell.tsx). */}
            <nav className="tabs md:hidden" aria-label="Price List">
                {meta.sections?.map((s) => (
                    <Link
                        key={s.id}
                        href={`${BASE}/${s.id}` + (params.toString() ? "?" + params.toString() : "")}
                        aria-current={section === s.id ? "page" : undefined}
                        className="tab"
                    >
                        <Icon name={s.icon} className="size-4" />
                        {s.name}
                    </Link>
                ))}
            </nav>
            {section === "materials" && (
                <MaterialsList key="items" data={data} openUuid={params.get("item") ?? undefined} onOpen={(uuid) => windows.openItem(uuid)} onNew={() => set({ item: "new", itemTab: null })} />
            )}
            {section === "bundles" && (
                <BundlesList data={data} openUuid={params.get("bundle") ?? undefined} onOpen={(uuid) => windows.openBundle(uuid)} onNew={() => set({ bundle: "new", bundleTab: null })} />
            )}
            {section === "products" && (
                <MaterialsList key="products" products data={data} openUuid={params.get("product") ?? undefined} onOpen={(uuid) => windows.openProduct(uuid)} onNew={() => set({ product: "new", productTab: null })} />
            )}
            {section === "groups" && <GroupsList data={data} openId={params.get("group") ?? undefined} onOpen={(id, options) => windows.openGroup(id, options)} />}
            {section === "suppliers" && (
                <SuppliersList
                    data={data}
                    openUuid={params.get("supplier") ?? undefined}
                    onOpen={(uuid) => windows.openSupplier(uuid)}
                    onNew={() => set({ supplier: "new", supplierTab: null })}
                />
            )}
        </>
    );
}
